import {
  AUTOMATION_DESCRIPTION_MAX,
  AUTOMATION_NAME_MAX,
  type AutomationAction,
  type AutomationRule,
  type AutomationTrigger,
  automationRuleIssues,
  type ConditionRule,
} from '@servicetim/shared';
import {
  useCreateAutomationRule,
  useProjectRoles,
  useProjects,
  useSlaPolicies,
  useUpdateAutomationRule,
} from '@servicetim/ui/api';
import {
  Badge,
  Button,
  Input,
  ProblemBanner,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  toast,
} from '@servicetim/ui/components';
import { useEffect, useId, useState } from 'react';
import { useFieldCatalog } from '../projects/catalog/field-catalog';
import { fieldErrorsOf, messageAt } from '../projects/lib/field-errors';
import { FormRow } from '../projects/ui/form-row';
import { InlineErrors } from '../projects/ui/inline-errors';
import { SimpleSelect } from '../projects/ui/simple-select';
import { ActionsEditor } from './actions-editor';
import { RuleConditionsEditor } from './rule-conditions-editor';
import { RuleRunsPanel } from './rule-runs-panel';
import { RuleTestPanel } from './rule-test-panel';
import { newTrigger, TriggerEditor } from './trigger-editor';

export interface RuleEditorSheetProps {
  /** `null` creates a rule; `undefined` keeps the sheet closed. */
  rule: AutomationRule | null | undefined;
  /** Fixed project scope (the project's "Automations" tab); `undefined` lets the user choose. */
  lockedProjectId?: string;
  onOpenChange: (open: boolean) => void;
}

/**
 * Create/edit sheet for one automation rule: name/enabled, the trigger, conditions (reusing the
 * workflow `ConditionBuilder`), the actions list, a read-only JSON view, and — once the rule is
 * saved — a dry-run Test tab and the Runs history (S-07-01 … S-07-04, S-07-12).
 */
export function RuleEditorSheet({ rule, lockedProjectId, onOpenChange }: RuleEditorSheetProps) {
  const id = useId();
  const create = useCreateAutomationRule();
  const update = useUpdateAutomationRule();
  const open = rule !== undefined;
  const isSystem = rule?.isSystem ?? false;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [scope, setScope] = useState<'tenant' | 'project'>(lockedProjectId ? 'project' : 'tenant');
  const [projectId, setProjectId] = useState<string | null>(lockedProjectId ?? null);
  const [trigger, setTrigger] = useState<AutomationTrigger>(() => newTrigger('event'));
  const [conditions, setConditions] = useState<ConditionRule[]>([]);
  const [actions, setActions] = useState<AutomationAction[]>([]);
  const [tab, setTab] = useState('rule');

  const projects = useProjects({ pageSize: 200 });
  const effectiveProjectId = scope === 'project' ? (projectId ?? undefined) : undefined;
  const catalog = useFieldCatalog(effectiveProjectId);
  const roles = useProjectRoles(effectiveProjectId);
  const slaPolicies = useSlaPolicies(effectiveProjectId);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset the draft when opened or the row changes
  useEffect(() => {
    if (!open) return;
    setName(rule?.name ?? '');
    setDescription(rule?.description ?? '');
    setEnabled(rule?.enabled ?? true);
    const nextScope = rule
      ? rule.projectId
        ? 'project'
        : 'tenant'
      : lockedProjectId
        ? 'project'
        : 'tenant';
    setScope(nextScope);
    setProjectId(rule ? rule.projectId : (lockedProjectId ?? null));
    setTrigger(rule?.trigger ?? newTrigger('event'));
    setConditions(rule?.conditions ?? []);
    setActions(rule?.actions ?? []);
    setTab('rule');
    create.reset();
    update.reset();
  }, [open, rule?.id, lockedProjectId]);

  const error = create.error ?? update.error;
  const serverErrors = fieldErrorsOf(error);
  const clientIssues = automationRuleIssues({ trigger, actions });
  const nameEmpty = name.trim() === '';
  const projectMissing = scope === 'project' && !projectId;
  const actionsEmpty = actions.length === 0;
  const invalid =
    nameEmpty || projectMissing || (!isSystem && (actionsEmpty || clientIssues.length > 0));

  const save = async () => {
    if (invalid) return;
    const draft = {
      name: name.trim(),
      description: description.trim() === '' ? null : description.trim(),
      enabled,
      trigger,
      conditions,
      actions,
    };
    try {
      if (rule) {
        await update.mutateAsync({ id: rule.id, patch: isSystem ? { enabled } : draft });
        toast.success(`"${name.trim()}" saved`);
      } else {
        await create.mutateAsync({
          ...draft,
          projectId: scope === 'project' ? (projectId ?? null) : null,
        });
        toast.success(`"${name.trim()}" created`);
      }
      onOpenChange(false);
    } catch {
      // banner
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-3xl lg:max-w-5xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {rule ? `Edit "${rule.name}"` : 'New automation rule'}
            {isSystem ? <Badge variant="secondary">System rule</Badge> : null}
          </SheetTitle>
          <SheetDescription>
            {isSystem
              ? 'System rules are seeded defaults: only enabling/disabling is allowed here.'
              : 'Trigger, conditions and actions run as closed JSON — nothing here is a script.'}
          </SheetDescription>
        </SheetHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex-1">
          <TabsList>
            <TabsTrigger value="rule">Rule</TabsTrigger>
            <TabsTrigger value="json">JSON</TabsTrigger>
            <TabsTrigger value="test" disabled={!rule}>
              Test
            </TabsTrigger>
            <TabsTrigger value="runs" disabled={!rule}>
              Runs
            </TabsTrigger>
          </TabsList>

          <TabsContent value="rule" className="space-y-5">
            {error && clientIssues.length === 0 ? (
              <ProblemBanner error={error} size="sm" omitPaths={serverErrors.map((e) => e.path)} />
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              <FormRow
                label="Name"
                htmlFor={`${id}-name`}
                required
                error={messageAt(serverErrors, 'name')}
              >
                <Input
                  id={`${id}-name`}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={AUTOMATION_NAME_MAX}
                  disabled={isSystem}
                  autoFocus
                />
              </FormRow>
              <FormRow label="Enabled" htmlFor={`${id}-enabled`} inline>
                <Switch id={`${id}-enabled`} checked={enabled} onCheckedChange={setEnabled} />
              </FormRow>
            </div>
            <FormRow label="Description (optional)" htmlFor={`${id}-description`}>
              <Textarea
                id={`${id}-description`}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={AUTOMATION_DESCRIPTION_MAX}
                rows={2}
                disabled={isSystem}
              />
            </FormRow>
            {!lockedProjectId ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <FormRow label="Scope" htmlFor={`${id}-scope`}>
                  <SimpleSelect
                    id={`${id}-scope`}
                    value={scope}
                    disabled={rule !== null}
                    options={[
                      { value: 'tenant', label: 'Tenant-wide' },
                      { value: 'project', label: 'One project' },
                    ]}
                    onChange={(next) => next && setScope(next as 'tenant' | 'project')}
                  />
                </FormRow>
                {scope === 'project' ? (
                  <FormRow
                    label="Project"
                    htmlFor={`${id}-project`}
                    required
                    error={projectMissing ? 'Choose a project' : undefined}
                  >
                    <SimpleSelect
                      id={`${id}-project`}
                      value={projectId ?? undefined}
                      disabled={rule !== null}
                      placeholder="Choose a project…"
                      options={projects.items.map((p) => ({
                        value: p.id,
                        label: `${p.key} · ${p.name}`,
                      }))}
                      onChange={(next) => next && setProjectId(next)}
                    />
                  </FormRow>
                ) : null}
              </div>
            ) : null}

            <fieldset disabled={isSystem} className="space-y-5">
              <section className="space-y-2">
                <h3 className="font-semibold text-fg text-sm">Trigger</h3>
                <TriggerEditor
                  value={trigger}
                  onChange={setTrigger}
                  catalog={catalog}
                  slaPolicies={slaPolicies.data ?? []}
                  idPrefix={`${id}-trigger`}
                />
              </section>

              <section className="space-y-2">
                <h3 className="font-semibold text-fg text-sm">Conditions</h3>
                <p className="text-fg-muted text-xs">
                  All conditions below must hold (empty = always).
                </p>
                <RuleConditionsEditor
                  value={conditions}
                  onChange={setConditions}
                  catalog={catalog}
                  roles={roles.data ?? []}
                  idPrefix={`${id}-cond`}
                />
              </section>

              <section className="space-y-2">
                <h3 className="font-semibold text-fg text-sm">Actions</h3>
                <ActionsEditor
                  value={actions}
                  onChange={setActions}
                  catalog={catalog}
                  projectId={scope === 'project' ? projectId : null}
                  scheduleRule={trigger.type === 'schedule'}
                  idPrefix={`${id}-action`}
                />
              </section>
            </fieldset>

            <InlineErrors
              errors={clientIssues.map((i) => ({ path: i.path, message: i.message }))}
            />
          </TabsContent>

          <TabsContent value="json" className="space-y-2">
            <p className="text-fg-muted text-xs">The exact JSON the API stores for this rule.</p>
            <pre className="max-h-[32rem] overflow-auto rounded-md border border-border bg-surface-sunken p-3 text-xs">
              {JSON.stringify(
                { name, description, enabled, trigger, conditions, actions },
                null,
                2,
              )}
            </pre>
          </TabsContent>

          <TabsContent value="test">{rule ? <RuleTestPanel ruleId={rule.id} /> : null}</TabsContent>
          <TabsContent value="runs">{rule ? <RuleRunsPanel ruleId={rule.id} /> : null}</TabsContent>
        </Tabs>

        {tab === 'rule' ? (
          <SheetFooter className="flex-row justify-end">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void save()}
              disabled={create.isPending || update.isPending || invalid}
            >
              {rule ? 'Save rule' : 'Create rule'}
            </Button>
          </SheetFooter>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
